import { useState, useRef, DragEvent } from 'react';

interface PhotoUploadProps {
  currentPhoto: string | null;
  hcpAvatar: string | null;
  employeeName: string;
  onUpload: (photo: string, contentType: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

const MAX_DIMENSION = 400; // Resize to 400x400 max

export function PhotoUpload({ currentPhoto, hcpAvatar, employeeName, onUpload, onDelete }: PhotoUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resizeImage = (file: File): Promise<{ data: string; type: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Scale down if needed
          if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_DIMENSION;
              width = MAX_DIMENSION;
            } else {
              width = (width / height) * MAX_DIMENSION;
              height = MAX_DIMENSION;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to JPEG for smaller size
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          const base64 = dataUrl.split(',')[1];

          resolve({ data: base64, type: 'image/jpeg' });
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file: File) => {
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const { data, type } = await resizeImage(file);
      await onUpload(data, type);
    } catch (err) {
      setError('Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Get display photo: custom > HCP avatar > placeholder
  const getDisplayPhoto = (): string | null => {
    if (currentPhoto) {
      return `data:image/jpeg;base64,${currentPhoto}`;
    }
    if (hcpAvatar && !hcpAvatar.includes('add_image_thumb')) {
      return hcpAvatar;
    }
    return null;
  };

  const displayPhoto = getDisplayPhoto();
  const initials = employeeName.split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <div className="photo-upload">
      <div
        className={`photo-drop-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => !uploading && inputRef.current?.click()}
        title="Click or drag to upload photo"
      >
        {displayPhoto ? (
          <img src={displayPhoto} alt={employeeName} className="photo-preview" />
        ) : (
          <div className="photo-placeholder">{initials}</div>
        )}
        {uploading && <div className="photo-loading">...</div>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
        style={{ display: 'none' }}
      />
      {error && <div className="photo-error">{error}</div>}
      {currentPhoto && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="photo-remove"
          title="Remove custom photo"
        >
          Remove
        </button>
      )}
    </div>
  );
}
